import { Button, Flex, Text } from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { CreateLeadBlock } from "@/components/LeadBlock/CreateLeadBlock/CreateLeadBlock";
import { Layout } from "@/components/Layout/Layout";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import api from "@/api";
import { LeadContact, LeadForm as ModelLeadForm } from "@/model/lead";

export interface LeadForm {
  name: string;
  contact_id: number | null;
  responsible_user_id: number | null;
  source: string | null;
  website: string;
  comment: string;
  nda: string | null;
  questionnaire: string | null;
  nda_call: string | null;
  status: string | null;
}

export const CreateLeadPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<LeadContact[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const transformFormToApi = (values: LeadForm): ModelLeadForm | null => {
    if (values.responsible_user_id === null || values.status === null) {
      return null;
    }

    const statusId = Number(values.status);
    const sourceId = values.source ? Number(values.source) : null;

    if (isNaN(statusId) || (sourceId !== null && isNaN(sourceId))) {
      return null;
    }

    return {
      name: values.name,
      responsible_user_id: values.responsible_user_id,
      status_id: statusId,
      contact_id: values.contact_id || null,
      source_id: sourceId,
      site: values.website || null,
      comments: values.comment || null,
      nda: values.nda === "yes" ? true : values.nda === "no" ? false : null,
      form: values.questionnaire === "yes" ? true : values.questionnaire === "no" ? false : null,
      nda_call: values.nda_call === "yes" ? true : values.nda_call === "no" ? false : null,
    };
  };

  const handleCreate = async () => {
    if (formik.values.name === "" || formik.values.responsible_user_id === null || formik.values.status === null) {
      return;
    }

    setIsCreating(true);
    try {
      const apiData = transformFormToApi(formik.values);
      if (!apiData) {
        return;
      }
      const response = await api.leads.leadCreate(apiData);
      if (response.data?.id) {
        navigate(`/leads/${response.data.id}`);
      }
    } catch (error) {
      console.error("Ошибка при создании лида:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const formik = useFormik<LeadForm>({
    initialValues: {
      name: "",
      contact_id: null,
      responsible_user_id: null,
      source: null,
      website: "",
      comment: "",
      nda: "no",
      questionnaire: "no",
      nda_call: "no",
      status: null,
    },
    onSubmit: handleCreate,
  });

  useEffect(() => {
    api.leads
      .leadContacts()
      .then((res) => {
        setContacts(res.data);
      })
      .catch((err) => {
        console.error("Ошибка при загрузке контактов:", err);
      });
  }, []);

  return (
    <Layout>
      <Flex flexDirection="column" gap="24px" paddingRight="32px" maxH="90vh" overflowY="auto">
        <Text color="gray.500" cursor="pointer" onClick={() => navigate("/leads")} display="flex" alignItems="center" gap="8px">
          <ArrowBackIcon />К списку лидов
        </Text>
        <Flex flexDirection="column" gap="4px">
          <Text fontSize={30} lineHeight="36px" fontWeight={700} color="#3b515e">
            Создание лида
          </Text>
          <Text fontSize={16} lineHeight="24px" fontWeight={400} color="#65758b">
            Заполните информацию о новом лиде
          </Text>
        </Flex>
        <CreateLeadBlock formik={formik} contacts={contacts} />
        <Flex gap="16px" justifyContent="flex-end">
          <Button onClick={() => navigate("/leads")}>Отмена</Button>
          <Button
            bg="#2563eb"
            color="white"
            _hover={{ bg: "#2563eb" }}
            _active={{ bg: "#2563eb" }}
            onClick={handleCreate}
            isLoading={isCreating}
            isDisabled={formik.values.name === "" || formik.values.responsible_user_id === null || formik.values.status === null}
          >
            Создать лид
          </Button>
        </Flex>
      </Flex>
    </Layout>
  );
};
